import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, PhoneIncoming, ShieldCheck } from 'lucide-react';
import { fetchCallBehavior, saveCallBehavior } from '../../../api/ivrAgents.js';
import { overlayDismiss } from '../../../lib/overlayDismiss';
import { CallBehaviorSelector } from '../AddAvatarWizard.jsx';

/**
 * CallBehaviorModal — 既有角色的「通話行為」（能不能插話 / 客戶不出聲時 / 開場白後完全沒出聲）
 *
 * 以前只有新增角色精靈 step 2 看得到，既有角色只能跟 builder 對話改 → 客戶不知道能設（0908 君綺）。
 * 這裡直接 PUT /api/ivr-flow/call-behavior 寫 liveConfig（不經 builder AI），存完後端即清快取，下一通生效。
 *
 * 「目前生效」提示：avatar 沒明確設插話模式時，runtime 走預設（客服=開場白唸完再收、總機=可隨時插話），
 * 畫面會標出這一點，讓用戶知道自己現在吃的是預設值。
 */
export function CallBehaviorModal({ open, onClose, avatarId }) {
    const { t } = useTranslation('wsModals');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [interruptMode, setInterruptMode] = useState('protectGreeting');
    const [defaultMode, setDefaultMode] = useState('protectGreeting');
    const [interruptExplicit, setInterruptExplicit] = useState(false);
    const [repromptSec, setRepromptSec] = useState('20');
    const [noResponseSec, setNoResponseSec] = useState('0');
    const [noResponseAction, setNoResponseAction] = useState('transfer');
    const [noResponseExt, setNoResponseExt] = useState('');

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setError(''); setSaving(false); setLoading(true);
        fetchCallBehavior(avatarId)
            .then((r) => {
                if (cancelled || !r) return;
                setInterruptMode(r.interruptMode || 'protectGreeting');
                setDefaultMode(r.defaultInterruptMode || 'protectGreeting');
                setInterruptExplicit(r.interruptExplicit === true);
                setRepromptSec(String(Number.isFinite(Number(r.repromptSec)) ? r.repromptSec : 20));
                setNoResponseSec(String(Number.isFinite(Number(r.noResponseSec)) ? r.noResponseSec : 0));
                setNoResponseAction(r.noResponseAction === 'hangup' ? 'hangup' : 'transfer');
                setNoResponseExt(r.noResponseExt || '');
            })
            .catch((e) => { if (!cancelled) setError(e.message || t('callBehavior.loadFailed')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, avatarId]);

    if (!open) return null;

    const handleSave = async () => {
        if (saving) return;
        const rp = Number(repromptSec);
        const nr = Number(noResponseSec);
        if (!Number.isFinite(rp) || rp < 0 || rp > 120 || !Number.isFinite(nr) || nr < 0 || nr > 120) {
            setError(t('callBehavior.rangeError')); return;
        }
        setError(''); setSaving(true);
        try {
            await saveCallBehavior(avatarId, {
                interruptMode,
                repromptSec: Math.round(rp),
                noResponseSec: Math.round(nr),
                noResponseAction,
                noResponseExt: noResponseAction === 'transfer' ? noResponseExt.trim() : '',
            });
            onClose();
        } catch (e) {
            setError(e.message || 'save failed');
        } finally {
            setSaving(false);
        }
    };

    const defaultLabel = defaultMode === 'allow'
        ? t('callBehavior.defaultAllow')
        : t('callBehavior.defaultProtect');

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-2.5">
                        <div className="inline-flex items-center justify-center size-8 rounded-md bg-accent-50">
                            <PhoneIncoming size={17} strokeWidth={1.6} className="text-accent-700" />
                        </div>
                        <div>
                            <div className="text-sm font-medium text-ink-900">{t('callBehavior.title')}</div>
                            <div className="text-[11px] text-ink-400">{t('callBehavior.subtitle')}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-cream-100 text-ink-400 hover:text-ink-700 transition">
                        <X size={16} />
                    </button>
                </div>

                {/* body */}
                <div className={['flex-1 overflow-y-auto px-5 py-4 space-y-4', loading ? 'opacity-50 pointer-events-none' : ''].join(' ')}>
                    {/* 目前生效提示 */}
                    <div className="flex items-start gap-2 rounded-md bg-accent-50 border border-accent-200 px-3 py-2.5">
                        <ShieldCheck size={14} className="text-accent-600 shrink-0 mt-0.5" />
                        <div className="text-[11px] text-ink-500 leading-snug">
                            {interruptExplicit
                                ? t('callBehavior.explicitNote')
                                : t('callBehavior.defaultNote', { mode: defaultLabel })}
                        </div>
                    </div>

                    <CallBehaviorSelector
                        interruptMode={interruptMode} setInterruptMode={setInterruptMode}
                        repromptSec={repromptSec} setRepromptSec={setRepromptSec}
                        noResponseSec={noResponseSec} setNoResponseSec={setNoResponseSec}
                        noResponseAction={noResponseAction} setNoResponseAction={setNoResponseAction}
                        noResponseExt={noResponseExt} setNoResponseExt={setNoResponseExt}
                        defaultMode={defaultMode}
                    />

                    <div className="text-[11px] text-ink-400 leading-snug">{t('callBehavior.effectNote')}</div>
                    {error && <div className="text-[12px] text-danger-500">{error}</div>}
                </div>

                {/* footer */}
                <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-cream-200">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-md text-ink-500 hover:bg-cream-100 transition">{t('callBehavior.cancel')}</button>
                    <button onClick={handleSave} disabled={saving || loading} className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed">
                        {saving ? t('callBehavior.saving') : t('callBehavior.saveBtn')}
                    </button>
                </div>
            </div>
        </div>
    );
}
